/**
 * FRVR SDK — bootstrap, ads, lifecycle, storage, share.
 * Non-FRVR builds set VITE_WITH_FRVR=false so this module never touches window.FRVR.
 */

import { INTERSTITIAL_AD_NAME_NEXT_DAY } from "../config/ad-placements.js";
import { devLog } from "./dev-log.js";
import {
  clearFrvrGameStorage,
  createFrvrGameStorage,
  flushFrvrGameStorageNow,
  hydrateFrvrGameStorage,
  seedFrvrLeaderboardPlayerId,
} from "./frvr-persistence.js";
import { resolveGameStorage } from "./persistence-backend.js";

var initPromise = null;
var enabled = false;
var bootstrapPromise = null;
var loadingFinished = false;
var pauseSyncInstalled = false;
var audioSyncInstalled = false;

var REWARDED_READY_MAX_POLLS = 3;
var REWARDED_READY_RETRY_MS = 2000;
var REWARDED_READY_TTL_MS = 30000;
var rewardedReadyCache = { ready: false, updatedAt: 0 };
var rewardedReadyRefreshPromise = null;

function isFrvrBuild() {
  return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WITH_FRVR === "true";
}

function getSdk() {
  if (!isFrvrBuild() || typeof window === "undefined") return null;
  return window.FRVR || null;
}

function refreshEnabled() {
  enabled = !!(isFrvrBuild() && getSdk());
  return enabled;
}

function getBootstrapPromise() {
  if (bootstrapPromise) return bootstrapPromise;
  var sdk = getSdk();
  if (!sdk || !sdk.bootstrapper || typeof sdk.bootstrapper.init !== "function") {
    bootstrapPromise = Promise.resolve();
    return bootstrapPromise;
  }
  bootstrapPromise = Promise.resolve(sdk.bootstrapper.init());
  bootstrapPromise.then(function () {
    try {
      if (typeof sdk.bootstrapper.setProgress === "function") {
        sdk.bootstrapper.setProgress(0.5);
      }
    } catch (e) {
      console.warn("[FrvrBridge] bootstrapper.setProgress failed:", e);
    }
  }).catch(function (err) {
    console.warn("[FrvrBridge] bootstrapper.init failed:", err);
  });
  return bootstrapPromise;
}

function queryRewardedReadyOnce() {
  var sdk = getSdk();
  if (!sdk || !sdk.ads || typeof sdk.ads.isReady !== "function") return false;
  try {
    var rewardType = sdk.ads.AdType && sdk.ads.AdType.REWARD;
    return !!sdk.ads.isReady(rewardType);
  } catch (e) {
    console.warn("[FrvrBridge] ads.isReady failed:", e);
    return false;
  }
}

function withAdPause(game, startFn) {
  if (game && typeof game.setAdPaused === "function") {
    game.setAdPaused(true);
  }
  if (typeof startFn === "function") startFn();
}

function clearAdPause(game) {
  if (game && typeof game.setAdPaused === "function") {
    game.setAdPaused(false);
  }
}

function applyPlatformMute(soundManager, muted) {
  if (!soundManager) return;
  if (typeof soundManager.setPlatformMuted === "function") {
    soundManager.setPlatformMuted(!!muted);
    return;
  }
  if (typeof soundManager.setMuted === "function") {
    soundManager.setMuted(!!muted);
  }
}

/**
 * FRVR.profile.id() is async in the SDK guide; accept sync or Promise return values.
 * @param {object} sdk
 * @returns {Promise<string | null>}
 */
function resolveProfileId(sdk) {
  if (!sdk || !sdk.profile || typeof sdk.profile.id !== "function") {
    return Promise.resolve(null);
  }
  try {
    return Promise.resolve(sdk.profile.id()).then(function (id) {
      return id != null && String(id) ? String(id) : null;
    });
  } catch (e) {
    console.warn("[FrvrBridge] profile.id failed:", e);
    return Promise.resolve(null);
  }
}

function callTrackerMethod(methodName, value) {
  if (!enabled) return;
  var sdk = getSdk();
  if (!sdk || !sdk.tracker) return;
  var fn = sdk.tracker[methodName];
  if (typeof fn !== "function") return;
  try {
    fn.call(sdk.tracker, value);
  } catch (e) {
    console.warn("[FrvrBridge] tracker." + methodName + " failed:", e);
  }
}

function callAuthMethod(methodName) {
  if (!enabled) return null;
  var sdk = getSdk();
  if (!sdk || !sdk.auth) return null;
  var fn = sdk.auth[methodName];
  if (typeof fn !== "function") return null;
  try {
    return fn.call(sdk.auth);
  } catch (e) {
    console.warn("[FrvrBridge] auth." + methodName + " failed:", e);
    return null;
  }
}

/**
 * @param {string} methodName
 * @returns {Promise<unknown>}
 */
function promiseAuthMethod(methodName) {
  if (!enabled) return Promise.resolve(null);
  var sdk = getSdk();
  if (!sdk || !sdk.auth) return Promise.resolve(null);
  var fn = sdk.auth[methodName];
  if (typeof fn !== "function") return Promise.resolve(null);
  try {
    return Promise.resolve(fn.call(sdk.auth)).catch(function (err) {
      console.warn("[FrvrBridge] auth." + methodName + " failed:", err);
      return null;
    });
  } catch (e) {
    console.warn("[FrvrBridge] auth." + methodName + " failed:", e);
    return Promise.resolve(null);
  }
}

export function resetFrvrBridgeForTests() {
  initPromise = null;
  enabled = false;
  bootstrapPromise = null;
  loadingFinished = false;
  pauseSyncInstalled = false;
  audioSyncInstalled = false;
  rewardedReadyCache = { ready: false, updatedAt: 0 };
  rewardedReadyRefreshPromise = null;
  clearFrvrGameStorage();
}

export const FrvrBridge = {
  ensureInitialized: function () {
    if (initPromise) return initPromise;
    if (!isFrvrBuild()) {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    var sdk = getSdk();
    if (!sdk) {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    try {
      if (typeof sdk.init === "function") {
        sdk.init("prod");
      }
    } catch (e0) {
      console.warn("[FrvrBridge] FRVR.init failed:", e0);
    }
    initPromise = getBootstrapPromise().then(function () {
      refreshEnabled();
    });
    return initPromise;
  },

  isEnabled: function () {
    return !!enabled;
  },

  /**
   * Poll FRVR.ads.isReady(REWARD) up to three times (Smartle parity).
   * @returns {Promise<boolean>}
   */
  refreshRewardedAdReady: function () {
    if (rewardedReadyRefreshPromise) return rewardedReadyRefreshPromise;
    if (!enabled) {
      rewardedReadyCache = { ready: false, updatedAt: Date.now() };
      return Promise.resolve(false);
    }
    rewardedReadyRefreshPromise = FrvrBridge.ensureInitialized().then(function () {
      return getBootstrapPromise();
    }).then(function () {
      var sdk = getSdk();
      if (!sdk || !sdk.ads || typeof sdk.ads.isReady !== "function") {
        rewardedReadyCache = { ready: false, updatedAt: Date.now() };
        return false;
      }
      var ready = false;
      var attempt = 0;
      function step() {
        attempt += 1;
        ready = queryRewardedReadyOnce();
        if (ready || attempt >= REWARDED_READY_MAX_POLLS) {
          rewardedReadyCache = { ready: ready, updatedAt: Date.now() };
          return ready;
        }
        return new Promise(function (resolve) {
          setTimeout(resolve, REWARDED_READY_RETRY_MS);
        }).then(step);
      }
      return step();
    }).finally(function () {
      rewardedReadyRefreshPromise = null;
    });
    return rewardedReadyRefreshPromise;
  },

  /**
   * Cached FRVR.ads.isReady — refreshed on boot, after ads, and when stale (>30s).
   */
  canOfferRewardedAds: function () {
    if (!enabled) return false;
    var sdk = getSdk();
    if (!sdk || !sdk.ads || typeof sdk.ads.show !== "function") return false;
    var age = Date.now() - rewardedReadyCache.updatedAt;
    if (rewardedReadyCache.updatedAt === 0 || age > REWARDED_READY_TTL_MS) {
      FrvrBridge.refreshRewardedAdReady();
    }
    return rewardedReadyCache.ready;
  },

  /**
   * @returns {Promise<string | null>}
   */
  getPlayerId: function () {
    if (!enabled) return Promise.resolve(null);
    return resolveProfileId(getSdk());
  },

  /**
   * Hydrate FRVR local + cloud storage and route all game saves through it.
   * @returns {Promise<Storage | null>}
   */
  installGameStorage: function () {
    return FrvrBridge.ensureInitialized().then(function () {
      if (!enabled) return null;
      var sdk = getSdk();
      if (!sdk) return null;
      return getBootstrapPromise().then(function () {
        return hydrateFrvrGameStorage(sdk).then(function () {
          return createFrvrGameStorage(sdk);
        });
      });
    });
  },

  /** Push queued FRVR local + cloud writes and deletes immediately. */
  flushGameStorage: function () {
    return flushFrvrGameStorageNow();
  },

  /**
   * @returns {Promise<string | null>}
   */
  seedLeaderboardPlayerId: function (storage) {
    return FrvrBridge.getPlayerId().then(function (platformId) {
      return seedFrvrLeaderboardPlayerId(function () {
        return platformId;
      }, storage || resolveGameStorage(null));
    });
  },

  trackLevelStart: function (levelId) {
    if (!levelId) return;
    callTrackerMethod("levelStart", String(levelId));
  },

  trackLevelEnd: function (levelId) {
    if (!levelId) return;
    callTrackerMethod("levelEnd", String(levelId));
  },

  supportsShare: function () {
    if (!enabled) return false;
    var sdk = getSdk();
    if (!sdk || !sdk.social || !sdk.social.platform) return false;
    try {
      var API = sdk.social.platform.API;
      var shareApi = API && API.shareMessage;
      if (shareApi === undefined) return false;
      return !!sdk.social.platform.isSupportedAPI(shareApi);
    } catch (e) {
      return false;
    }
  },

  /**
   * @param {{ headline?: string, text?: string, image?: string, cta?: string, entryPointData?: Record<string, unknown> }} payload
   * @returns {Promise<boolean>}
   */
  shareMessage: function (payload) {
    if (!enabled || !FrvrBridge.supportsShare()) return Promise.resolve(false);
    var sdk = getSdk();
    if (!sdk || !sdk.social || !sdk.social.platform
      || typeof sdk.social.platform.shareMessage !== "function") {
      return Promise.resolve(false);
    }
    return Promise.resolve(sdk.social.platform.shareMessage(payload || {})).then(
      function () {
        return true;
      },
      function (err) {
        console.warn("[FrvrBridge] shareMessage failed:", err);
        return false;
      }
    );
  },

  sendGameReady: function () {
    if (!enabled || loadingFinished) return Promise.resolve();
    var sdk = getSdk();
    if (!sdk) return Promise.resolve();
    loadingFinished = true;
    return getBootstrapPromise().then(function () {
      try {
        if (sdk.bootstrapper && typeof sdk.bootstrapper.setProgress === "function") {
          sdk.bootstrapper.setProgress(1);
        }
        if (sdk.bootstrapper && typeof sdk.bootstrapper.complete === "function") {
          sdk.bootstrapper.complete();
        }
        if (sdk.tracker && typeof sdk.tracker.gameReady === "function") {
          sdk.tracker.gameReady();
        }
        devLog("[FrvrBridge] loading finished / gameReady");
      } catch (e) {
        loadingFinished = false;
        console.warn("[FrvrBridge] sendGameReady failed:", e);
      }
    });
  },

  shouldShowPlayAgainInterstitial: function (game) {
    return !!(enabled && game);
  },

  /**
   * Whether FRVR.auth reports an active session (host auto-login or FRVR account).
   * @returns {boolean}
   */
  isLoggedIn: function () {
    return !!callAuthMethod("isLoggedIn");
  },

  /**
   * Whether the current channel exposes host-native login (e.g. Facebook Instant).
   * @returns {boolean}
   */
  isHostLoginAvailable: function () {
    return !!callAuthMethod("isHostLoginAvailable");
  },

  /**
   * @returns {Promise<string | null>}
   */
  getHostUserId: function () {
    return promiseAuthMethod("getHostUserID").then(function (id) {
      return id != null && String(id) ? String(id) : null;
    });
  },

  /**
   * @returns {Promise<object | null>}
   */
  getLoginStatus: function () {
    return promiseAuthMethod("getLoginStatus");
  },

  /**
   * @param {(loggedIn: boolean) => void} listener
   */
  addAuthStatusChangeListener: function (listener) {
    if (!enabled || typeof listener !== "function") return;
    var sdk = getSdk();
    if (!sdk || !sdk.auth || typeof sdk.auth.addStatusChangeListener !== "function") return;
    try {
      sdk.auth.addStatusChangeListener(listener);
    } catch (e) {
      console.warn("[FrvrBridge] auth.addStatusChangeListener failed:", e);
    }
  },

  showBrowseInterstitial: function (name, options) {
    return FrvrBridge._showInterstitial(name, options);
  },

  showNextInterstitial: function (name, options) {
    return FrvrBridge._showInterstitial(name || INTERSTITIAL_AD_NAME_NEXT_DAY, options);
  },

  _showInterstitial: function (name, options) {
    var opts = options || {};
    var game = opts.game || null;
    if (!enabled) return Promise.resolve();
    var sdk = getSdk();
    if (!sdk || !sdk.ads || typeof sdk.ads.show !== "function") {
      return Promise.resolve();
    }
    var adType = sdk.ads.AdType && sdk.ads.AdType.INTERSTITIAL;
    return getBootstrapPromise().then(function () {
      return new Promise(function (resolve) {
        withAdPause(game, function () {
          sdk.ads
            .show(adType)
            .then(function () {
              clearAdPause(game);
              FrvrBridge.refreshRewardedAdReady();
              resolve();
            })
            .catch(function (err) {
              console.warn("[FrvrBridge] interstitial failed:", name, err);
              clearAdPause(game);
              resolve();
            });
        });
      });
    });
  },

  requestRewardedAd: function (options) {
    var opts = options || {};
    var game = opts.game || null;
    var started = typeof opts.onStarted === "function" ? opts.onStarted : null;
    var finished = typeof opts.onFinished === "function" ? opts.onFinished : null;
    var onError = typeof opts.onError === "function" ? opts.onError : null;

    return FrvrBridge.ensureInitialized().then(function () {
      if (!enabled) {
        if (onError) onError(new Error("rewarded ad unavailable"));
        return { granted: false, cancelled: false };
      }
      return FrvrBridge.refreshRewardedAdReady().then(function (ready) {
        var sdk = getSdk();
        if (!ready || !sdk || !sdk.ads || typeof sdk.ads.show !== "function") {
          if (onError) onError(new Error("rewarded ad unavailable"));
          return { granted: false, cancelled: false };
        }
        var rewardType = sdk.ads.AdType && sdk.ads.AdType.REWARD;
        var completed = sdk.ads.AdShowResult && sdk.ads.AdShowResult.COMPLETED;
        var delivered = sdk.ads.AdShowResult && sdk.ads.AdShowResult.DELIVERED;
        return getBootstrapPromise().then(function () {
          return new Promise(function (resolve) {
            withAdPause(game, function () {
              if (started) started();
              sdk.ads
                .show(rewardType)
                .then(function (result) {
                  clearAdPause(game);
                  FrvrBridge.refreshRewardedAdReady();
                  if (result === completed) {
                    if (finished) finished();
                    resolve({ granted: true, cancelled: false });
                    return;
                  }
                  if (result === delivered) {
                    if (onError) onError("dismissed");
                    resolve({ granted: false, cancelled: true });
                    return;
                  }
                  if (onError) onError("failed");
                  resolve({ granted: false, cancelled: false });
                })
                .catch(function (err) {
                  clearAdPause(game);
                  FrvrBridge.refreshRewardedAdReady();
                  if (onError) onError(err);
                  resolve({ granted: false, cancelled: false });
                });
            });
          });
        });
      });
    });
  },

  installPauseSync: function (game) {
    if (pauseSyncInstalled) return Promise.resolve();
    if (!game || typeof game.setHostPaused !== "function") return Promise.resolve();

    return FrvrBridge.ensureInitialized().then(function () {
      if (pauseSyncInstalled) return;
      if (!enabled) return;
      var sdk = getSdk();
      if (!sdk || !sdk.lifecycle) return;

      pauseSyncInstalled = true;

      sdk.lifecycle.onGamePause = function () {
        game.setHostPaused(true);
      };
      sdk.lifecycle.onSuspend = function () {
        game.setHostPaused(true);
      };
      sdk.lifecycle.onResume = function () {
        game.setHostPaused(false);
      };
    });
  },

  installAudioSync: function (soundManager) {
    if (audioSyncInstalled) return;
    if (!soundManager) return;

    FrvrBridge.ensureInitialized().then(function () {
      if (audioSyncInstalled) return;
      if (!enabled) return;
      var sdk = getSdk();
      if (!sdk || !sdk.lifecycle) return;

      audioSyncInstalled = true;

      sdk.lifecycle.onAudioSuspend = function () {
        applyPlatformMute(soundManager, true);
      };
      sdk.lifecycle.onAudioResume = function () {
        applyPlatformMute(soundManager, false);
      };
    });
  },
};
